import { CheckOutlined, SearchOutlined } from "@ant-design/icons";
import { Input, Select } from "antd";
import { ReportStreamSelect } from "../../../hooks/useReportStream";
import {
	getReportStatusLabel,
	ReportStatusKey,
} from "../../../utils/reporting/reportStatusOptions";
import { getStatusColorStyle } from "./ReportStreamParts";

type ReportStreamFiguresProps = {
	// Names the group for screen readers, such as "Show audits by status".
	label: string;
	figures: readonly ReportStatusKey[];
	selected: readonly ReportStatusKey[];
	// Unknown until the reports load.
	counts?: Partial<Record<ReportStatusKey, number>>;
	onChange: (selected: ReportStatusKey[]) => void;
};

// One figure per status, each a button that shows or hides the reports at
// that status: tinted with a tick while they show.
export function ReportStreamFigures({
	label,
	figures,
	selected,
	counts,
	onChange,
}: Readonly<ReportStreamFiguresProps>) {
	return (
		<fieldset className="report-stream__figures">
			<legend className="sr-only">{label}</legend>
			{figures.map((key) => {
				const isOn = selected.includes(key);
				return (
					<button
						key={key}
						type="button"
						className={
							isOn
								? "report-stream__figure report-stream__figure--on"
								: "report-stream__figure"
						}
						style={getStatusColorStyle(key)}
						aria-pressed={isOn}
						onClick={() =>
							onChange(
								isOn
									? selected.filter((other) => other !== key)
									: [...selected, key]
							)
						}
					>
						<span className="report-stream__figure-label">
							<span className="report-stream__dot" aria-hidden="true" />
							{getReportStatusLabel(key)}
							{isOn && (
								<CheckOutlined
									className="report-stream__tick"
									aria-hidden="true"
								/>
							)}
						</span>
						<span className="report-stream__figure-count">
							{counts ? (counts[key] ?? 0) : "–"}
						</span>
					</button>
				);
			})}
		</fieldset>
	);
}

type ReportStreamToolbarProps = {
	searchLabel: string;
	searchPlaceholder: string;
	searchValue: string;
	onSearchChange: (value: string) => void;
	selects: readonly ReportStreamSelect[];
};

// The search box and the filters.
export function ReportStreamToolbar({
	searchLabel,
	searchPlaceholder,
	searchValue,
	onSearchChange,
	selects,
}: Readonly<ReportStreamToolbarProps>) {
	return (
		<div className="report-stream__toolbar">
			<Input
				className="report-stream__search"
				prefix={<SearchOutlined aria-hidden="true" />}
				allowClear
				placeholder={searchPlaceholder}
				aria-label={searchLabel}
				value={searchValue}
				onChange={(event) => onSearchChange(event.target.value)}
			/>
			{selects.map((select) => (
				<Select
					key={select.key}
					className="report-stream__select"
					mode="multiple"
					allowClear
					maxTagCount="responsive"
					optionFilterProp="label"
					popupMatchSelectWidth={false}
					placeholder={select.label}
					aria-label={`Filter by ${select.label.toLowerCase()}`}
					options={select.options}
					value={select.value}
					onChange={select.onChange}
				/>
			))}
		</div>
	);
}
